/**
 * WhatsApp template mapping (ADR-037 phase 0A) — pure helpers for the
 * GoHire-admin Templates section and mapping drawer on /companies.
 *
 * A template body carries positional `{{n}}` placeholders. Each one is mapped
 * to a source the backend resolves at send time:
 *   merge   key = mail-merge token (workday_position, recruiter_name, brandname, …)
 *   contact key = first_name | last_name | name | mobile
 *   text    the recruiter's typed message
 *   event   key = when | title (interview reminders)
 *   input   label typed by the recruiter in the New Message dialog
 *   static  key = the fixed text itself
 * merge/contact params may also carry `ask: true` + `label`: the recruiter is
 * asked when sending and the field fills the slot when left empty.
 *
 * QUICK_REPLY buttons carry a reply value (payload): what GoHire receives when
 * the candidate taps the button, routed into the bot flow (e.g. "1" for Confirm).
 */

export type TemplateKind = 'reengage' | 'interview_invite' | 'interview_reminder';
export type ParamSource = 'merge' | 'contact' | 'text' | 'event' | 'input' | 'static';
export type MappingState = 'mapped' | 'needs_mapping' | 'unsupported' | 'not_approved' | 'removed';

export interface TemplateParam {
  source: ParamSource | string;
  key?: string | null;
  label?: string;
  fallback?: string;
  /** merge/contact only: ask the recruiter when sending; the field fills it when left empty. */
  ask?: boolean;
}

/** A quick-reply button and the reply value GoHire receives when it is tapped. */
export interface TemplateButton {
  /** Position in the template's buttons (as the backend reports it). */
  index: number;
  text: string;
  payload: string;
}

export const BUTTON_PAYLOAD_MAX = 128;

export interface SenderTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  body: string;
  placeholderCount: number;
  supported: boolean;
  unsupportedReason?: string | null;
  kind: TemplateKind | null;
  kindLabel?: string;
  params: TemplateParam[];
  /** QUICK_REPLY buttons with their reply values. */
  buttons?: TemplateButton[];
  mappingState: MappingState | string;
  hasTextParam?: boolean;
  syncedAt?: string | null;
  removedAt?: string | null;
}

export interface MergeField {
  key: string | null;
  label?: string;
  group?: string;
  source?: string;
}

/** GET …/whatsapp/merge-fields. `custom` entries may be bare keys or MergeField objects. */
export interface MergeFields {
  standard: MergeField[];
  custom: (string | MergeField)[];
}

// ── API shape normalisation ──────────────────────────────────────────

/** `buttons` from the API → [{ index, text, payload }] (payload defaults to the text). */
export function normalizeButtons(raw: any): TemplateButton[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((b) => b && typeof b === 'object')
    .map((b, i) => {
      const text = String(b.text || '');
      const payload = b.payload === undefined || b.payload === null ? text : String(b.payload);
      return { index: Number.isFinite(Number(b.index)) ? Number(b.index) : i, text, payload };
    });
}

/** One template row as the admin API returns it (`mappingStatus` or `mappingState`). */
export function normalizeTemplate(raw: any): SenderTemplate {
  const t = raw || {};
  return {
    ...t,
    params: Array.isArray(t.params) ? t.params : [],
    buttons: normalizeButtons(t.buttons),
    placeholderCount: Number(t.placeholderCount || 0),
    kind: t.kind || null,
    mappingState: t.mappingState || t.mappingStatus || '',
  };
}

/** The list endpoint returns `[Template]` or `{ templates, lastSyncedAt }`. */
export function normalizeTemplateList(res: any): { templates: SenderTemplate[]; lastSyncedAt: string | null } {
  let rows: any[] = [];
  if (Array.isArray(res)) rows = res;
  else if (res && Array.isArray(res.templates)) rows = res.templates;
  const templates = rows.map(normalizeTemplate);
  const fromRows = templates.reduce<string | null>(
    (max, t) => (t.syncedAt && (!max || String(t.syncedAt) > max) ? String(t.syncedAt) : max),
    null,
  );
  return { templates, lastSyncedAt: (res && !Array.isArray(res) && res.lastSyncedAt) || fromRows };
}

/** Sync summary counts may be numbers or arrays of template names. */
export const countOf = (v: any): number => (Array.isArray(v) ? v.length : Number(v || 0));

// ── Body → chips ─────────────────────────────────────────────────────

export type BodySegment = { type: 'text'; text: string } | { type: 'slot'; index: number };

/** "Hi {{1}}, from {{2}}" → text / slot / text / slot segments (empty text runs dropped). */
export function splitTemplateBody(body: string): BodySegment[] {
  const out: BodySegment[] = [];
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  const src = String(body || '');
  let last = 0;
  let m: RegExpExecArray | null = re.exec(src);
  while (m) {
    if (m.index > last) out.push({ type: 'text', text: src.slice(last, m.index) });
    out.push({ type: 'slot', index: Number(m[1]) });
    last = m.index + m[0].length;
    m = re.exec(src);
  }
  if (last < src.length) out.push({ type: 'text', text: src.slice(last) });
  return out;
}

/** Highest `{{n}}` in the body (placeholders are 1-based and contiguous on Meta templates). */
export function countPlaceholders(body: string): number {
  return splitTemplateBody(body).reduce((max, s) => (s.type === 'slot' && s.index > max ? s.index : max), 0);
}

// ── Source choices ───────────────────────────────────────────────────

/** A Select value is "<source>:<key>"; `input` / `static` carry their text in a separate field. */
export const choiceValue = (source: string, key = ''): string => `${source}:${key}`;

export function decodeChoice(value: string): { source: string; key: string } {
  const i = String(value || '').indexOf(':');
  if (i < 0) return { source: value || '', key: '' };
  return { source: value.slice(0, i), key: value.slice(i + 1) };
}

/** Synonyms that land on one of the fixed options (older mappings, mail-merge spellings). */
const CHOICE_SYNONYMS: Record<string, string> = {
  'merge:firstname': 'contact:first_name',
  'merge:first_name': 'contact:first_name',
  'merge:lastname': 'contact:last_name',
  'merge:last_name': 'contact:last_name',
  'merge:name': 'contact:name',
  'merge:mobile': 'contact:mobile',
  'merge:company': 'merge:brandname',
  'merge:company_name': 'merge:brandname',
  'merge:companyname': 'merge:brandname',
};

/** Select value for a stored param (input/static/text collapse to one option each). */
export function paramChoice(p?: TemplateParam | null): string | undefined {
  if (!p || !p.source) return undefined;
  if (p.source === 'input' || p.source === 'static' || p.source === 'text') return choiceValue(p.source);
  const v = choiceValue(p.source, p.key || '');
  return CHOICE_SYNONYMS[v] || v;
}

export interface ChoiceOption { value: string; label: string }
export interface ChoiceGroup { label: string; options: ChoiceOption[] }

const CONTACT_FIXED: ChoiceOption[] = [
  { value: choiceValue('contact', 'first_name'), label: 'First name' },
  { value: choiceValue('contact', 'last_name'), label: 'Last name' },
  { value: choiceValue('contact', 'name'), label: 'Full name' },
  { value: choiceValue('contact', 'mobile'), label: 'Mobile' },
];
const RECRUITER_FIXED: ChoiceOption[] = [
  { value: choiceValue('merge', 'recruiter_name'), label: 'Recruiter name' },
  { value: choiceValue('merge', 'recruiter_email'), label: 'Recruiter email' },
];
const COMPANY_FIXED: ChoiceOption[] = [
  { value: choiceValue('merge', 'brandname'), label: 'Company name' },
];

/** Merge tokens already offered by the fixed options (or synonyms of them) — not repeated as custom fields. */
const FIXED_MERGE_KEYS = new Set([
  'firstname', 'first_name', 'lastname', 'last_name', 'name', 'fullname', 'mobile', 'phone',
  'recruiter_name', 'recruiter_email', 'recruitername', 'recruiteremail', 'username', 'useremail',
  'company', 'brandname', 'company_name', 'companyname', 'whatsappoptinattested',
]);

/** "workday_position" → "Workday position". */
export function humanizeKey(key: string): string {
  const words = String(key || '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * Grouped options for a placeholder's source Select: Contact (fixed + the
 * company's custom fields), Recruiter, Company, Message, Interview, then the
 * two free-form choices.
 */
export function buildSourceGroups(mergeFields?: MergeFields | null): ChoiceGroup[] {
  const contact = [...CONTACT_FIXED];
  const recruiter = [...RECRUITER_FIXED];
  const company = [...COMPANY_FIXED];
  const seenValues = new Set([...contact, ...recruiter, ...company].map((o) => o.value));
  const seenKeys = new Set(FIXED_MERGE_KEYS);

  const add = (f: MergeField | string) => {
    const field: MergeField = typeof f === 'string' ? { key: f, source: 'merge' } : (f || { key: null });
    const source = field.source || 'merge';
    if (!field.key || (source !== 'merge' && source !== 'contact')) return;
    const raw = choiceValue(source, field.key);
    const value = CHOICE_SYNONYMS[raw] || raw;
    if (seenValues.has(value) || (source === 'merge' && seenKeys.has(field.key))) return;
    seenValues.add(value);
    seenKeys.add(field.key);
    const label = field.label && field.label !== field.key ? field.label : humanizeKey(field.key);
    const opt = { value, label };
    const g = String(field.group || 'contact').toLowerCase();
    if (g === 'recruiter' || g === 'user') recruiter.push(opt);
    else if (g === 'company') company.push(opt);
    else contact.push(opt);
  };

  const keyOfField = (f: string | MergeField): string => (typeof f === 'string' ? f : String((f && f.key) || ''));
  const standard = (mergeFields && Array.isArray(mergeFields.standard)) ? mergeFields.standard : [];
  standard.forEach(add);
  const custom = (mergeFields && Array.isArray(mergeFields.custom)) ? [...mergeFields.custom] : [];
  custom.sort((a, b) => keyOfField(a).localeCompare(keyOfField(b))).forEach(add);

  return [
    { label: 'Contact', options: contact },
    { label: 'Recruiter', options: recruiter },
    { label: 'Company', options: company },
    { label: 'Message', options: [{ value: choiceValue('text'), label: 'The recruiter’s typed message' }] },
    {
      label: 'Interview',
      options: [
        { value: choiceValue('event', 'when'), label: 'Interview date & time' },
        { value: choiceValue('event', 'title'), label: 'Interview title' },
      ],
    },
    { label: 'Ask when sending', options: [{ value: choiceValue('input'), label: 'Ask when sending' }] },
    { label: 'Fixed text', options: [{ value: choiceValue('static'), label: 'Fixed text' }] },
  ];
}

/** "Contact · First name" / "Recruiter name" — the label stored with the param and shown in the drawer. */
export function choiceLabel(value: string, groups: ChoiceGroup[]): string {
  for (let i = 0; i < groups.length; i += 1) {
    const opt = groups[i].options.find((o) => o.value === value);
    if (opt) {
      const group = groups[i].label;
      const named = opt.label === group || opt.label.toLowerCase().startsWith(group.toLowerCase());
      return named ? opt.label : `${group} · ${opt.label}`;
    }
  }
  const { key } = decodeChoice(value);
  return humanizeKey(key);
}

/** Sources that read candidate/company data and so must carry a fallback (Meta rejects empty params). */
export const needsFallback = (source: string): boolean => source === 'merge' || source === 'contact';

/** merge/contact fields can additionally be asked when sending. */
export const canAsk = (source: string): boolean => source === 'merge' || source === 'contact';

// ── Automatic use ────────────────────────────────────────────────────

export const MANUAL_ONLY = '__manual__';

export const KIND_OPTIONS: { value: string; label: string; help: string }[] = [
  {
    value: 'reengage',
    label: 'Continue conversation',
    help: 'Sent when a recruiter or follow-up messages a candidate whose 24-hour window has closed.',
  },
  {
    value: 'interview_reminder',
    label: 'Interview reminder',
    help: 'Sent by interview reminders when the candidate’s 24-hour window has closed.',
  },
  {
    value: 'interview_invite',
    label: 'Interview invite',
    help: 'Used to start a new WhatsApp conversation that invites a candidate to an interview.',
  },
  {
    value: MANUAL_ONLY,
    label: 'Manual only',
    help: 'Recruiters can pick it in the New Message dialog. Never sent automatically.',
  },
];

export const kindLabel = (kind: string | null | undefined): string => {
  const opt = KIND_OPTIONS.find((o) => o.value === (kind || MANUAL_ONLY));
  return opt ? opt.label : String(kind);
};

// ── Draft rows (one per placeholder) ─────────────────────────────────

export interface DraftSlot {
  /** Select value "<source>:<key>", undefined while unmapped. */
  choice?: string;
  /** Label for `input` (and for an asked merge/contact field), value for `static`. */
  extra: string;
  fallback: string;
  /** merge/contact: also ask the recruiter when sending (a typed value wins). */
  ask?: boolean;
}

export function draftFromParams(params: TemplateParam[] | undefined, count: number): DraftSlot[] {
  const list = Array.isArray(params) ? params : [];
  return Array.from({ length: Math.max(0, count) }, (_, i) => {
    const p = list[i];
    if (!p || typeof p !== 'object') return { choice: undefined, extra: '', fallback: '' };
    let extra = '';
    if (p.source === 'input') extra = p.label || p.key || '';
    else if (p.source === 'static') extra = p.key || '';
    else if (p.ask) extra = p.label || '';
    const slot: DraftSlot = { choice: paramChoice(p), extra, fallback: p.fallback || '' };
    if (p.ask && canAsk(String(p.source))) slot.ask = true;
    return slot;
  });
}

export function paramsFromDraft(slots: DraftSlot[], groups: ChoiceGroup[]): TemplateParam[] {
  return slots.map((s) => {
    const { source, key } = decodeChoice(s.choice || '');
    const fallback = s.fallback.trim();
    if (source === 'input') {
      const label = s.extra.trim();
      return {
        source, key: label, label, fallback,
      };
    }
    if (source === 'static') {
      return {
        source, key: s.extra, label: 'Fixed text', fallback,
      };
    }
    if (source === 'text') {
      return {
        source, key: '', label: 'Message', fallback,
      };
    }
    if (s.ask && canAsk(source)) {
      return {
        source, key, label: s.extra.trim(), fallback, ask: true,
      };
    }
    return {
      source, key, label: choiceLabel(s.choice || '', groups), fallback,
    };
  });
}

/** Per-slot error message (null when valid). `kind` null / MANUAL_ONLY = never sent automatically. */
export function validateSlot(s: DraftSlot, kind: string | null = null): string | null {
  if (!s.choice) return 'Choose what fills this placeholder';
  const { source } = decodeChoice(s.choice);
  if (source === 'input' && !s.extra.trim()) return 'Enter the label the recruiter sees';
  if (source === 'static' && !s.extra.trim()) return 'Enter the fixed text';
  if (s.ask && canAsk(source) && !s.extra.trim()) return 'Enter the label the recruiter sees';
  if (needsFallback(source) && !s.fallback.trim()) return 'A fallback is required for contact and company fields';
  const automatic = !!kind && kind !== MANUAL_ONLY;
  if (source === 'input' && automatic && !s.fallback.trim()) {
    return 'Automatic sends can’t ask the recruiter — add a fallback';
  }
  return null;
}

export function validateDraft(slots: DraftSlot[], kind: string | null = null): (string | null)[] {
  return slots.map((s) => validateSlot(s, kind));
}

export const draftIsValid = (slots: DraftSlot[], kind: string | null = null): boolean => (
  validateDraft(slots, kind).every((e) => !e)
);

export const hasTextSlot = (params: { source: string }[] | undefined | null): boolean => (
  Array.isArray(params) && params.some((p) => p && p.source === 'text')
);

/** Warning under "Automatic use" — Continue conversation without a Message slot holds the recruiter's text. */
export function kindWarning(kind: string | null | undefined, slots: DraftSlot[]): string | null {
  if (kind !== 'reengage') return null;
  const hasText = slots.some((s) => decodeChoice(s.choice || '').source === 'text');
  return hasText ? null : 'Recruiter messages are held and sent when the candidate replies.';
}

// ── Quick-reply buttons ──────────────────────────────────────────────

/** Error for one reply value (null when valid): 1–128 characters, not blank. */
export function validateButtonPayload(payload: string): string | null {
  const v = String(payload || '').trim();
  if (!v) return 'Enter the reply value';
  if (v.length > BUTTON_PAYLOAD_MAX) return `Keep it to ${BUTTON_PAYLOAD_MAX} characters`;
  return null;
}

export const buttonsAreValid = (buttons: TemplateButton[]): boolean => (
  buttons.every((b) => !validateButtonPayload(b.payload))
);

/** What PUT …/templates/{id} takes: [{ index, text, payload }] with trimmed payloads. */
export const buttonsForSave = (buttons: TemplateButton[]): TemplateButton[] => (
  buttons.map((b) => ({ index: b.index, text: b.text, payload: String(b.payload || '').trim() }))
);

// ── Table tags ───────────────────────────────────────────────────────

export const TEMPLATE_STATUS: Record<string, { label: string; color: string }> = {
  approved: { label: 'Approved', color: 'green' },
  submitted: { label: 'In review', color: 'blue' },
  rejected: { label: 'Rejected', color: 'red' },
  paused: { label: 'Paused', color: 'orange' },
  disabled: { label: 'Disabled', color: 'red' },
};

export const MAPPING_STATE: Record<string, { label: string; color: string }> = {
  mapped: { label: 'Mapped', color: 'green' },
  needs_mapping: { label: 'Needs mapping', color: 'gold' },
  unsupported: { label: 'Unsupported', color: 'default' },
  not_approved: { label: 'Not approved', color: 'default' },
  removed: { label: 'Removed', color: 'default' },
};

/** Rows that can be mapped (approved + supported + still on Infobip). */
export const isUsable = (t: SenderTemplate): boolean => (
  !t.removedAt && t.supported !== false && t.status === 'approved'
);

/** Why a greyed row can't be mapped. */
export function unusableReason(t: SenderTemplate): string | null {
  if (t.removedAt) return 'No longer on Infobip';
  if (t.supported === false) {
    return t.unsupportedReason
      ? `Unsupported: ${t.unsupportedReason}. GoHire fills body placeholders only.`
      : 'Has header or button variables, which GoHire cannot fill yet';
  }
  if (t.status !== 'approved') {
    return `${(TEMPLATE_STATUS[t.status] || { label: t.status }).label} by Meta — only approved templates can be sent`;
  }
  return null;
}
